import { useEffect, useState } from "react";
import { Check, ChevronDown, Loader2 } from "lucide-react";

function draftOf(item) {
  const a = item.assignedTo;
  if (a === "all") return { all: true, ppid: false, keys: [] };
  const list = Array.isArray(a) ? a : a ? [a] : [];
  return {
    all: false,
    ppid: list.includes("ppid"),
    keys: list.filter((k) => k !== "ppid"),
  };
}

// Nilai tersimpan dari draft: "all" | null (PPID isi sendiri) | daftar kunci.
function valueOf(d) {
  if (d.all) return "all";
  if (!d.keys.length) return null;
  return d.ppid ? ["ppid", ...d.keys] : d.keys;
}

export default function AssignDropdown({ item, accounts, busy, onSave }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => draftOf(item));

  useEffect(() => {
    if (open) setDraft(draftOf(item));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const divisions = (accounts || []).filter(
    (a) => a.key !== "ppid" && a.key !== "pts"
  );
  const labelOf = (k) => {
    if (k === "ppid") return "PPID (isi sendiri)";
    const a = (accounts || []).find((x) => x.key === k);
    return a ? a.label : k;
  };

  const saved = draftOf(item);
  const dirty =
    JSON.stringify(valueOf(draft)) !== JSON.stringify(valueOf(saved));

  const savedLabel = (() => {
    if (item.assignedTo === "all") return "Semua Pengelola";
    const keys = Array.isArray(item.assignedTo)
      ? item.assignedTo
      : item.assignedTo
        ? [item.assignedTo]
        : [];
    if (!keys.length) return "PPID (isi sendiri)";
    return keys.map(labelOf).join(", ");
  })();

  // PPID selalu menjadi pengisi default: saat belum ada divisi dipilih atau
  // "Semua Pengelola" aktif, centangan PPID tampil aktif dan terkunci.
  const ppidLocked = draft.all || !draft.keys.length;

  const toggleKey = (k) =>
    setDraft((d) => {
      const keys = d.keys.includes(k)
        ? d.keys.filter((x) => x !== k)
        : [...d.keys, k];
      return { all: false, ppid: d.ppid, keys };
    });

  const save = () => onSave(valueOf(draft), () => setOpen(false));

  const waiting = busy === `assign-${item.code}`;

  return (
    <div className="relative" data-testid={`monev-assign-${item.code}`}>
      <button
        type="button"
        data-testid={`monev-assign-trigger-${item.code}`}
        disabled={waiting}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex max-w-[300px] items-center gap-1.5 rounded-lg border border-[#E3EBF3] bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 outline-none transition-colors duration-200 hover:border-[#1479BF] hover:text-[#1479BF] disabled:opacity-50"
      >
        <span className="truncate" title={savedLabel}>
          {savedLabel}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-50 mt-2 max-h-[70vh] w-72 overflow-y-auto rounded-2xl border border-[#E3EBF3] bg-white p-3 shadow-xl">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">
              Tugaskan pengelola
            </p>
            <label
              className={`mt-2 flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors duration-150 ${
                ppidLocked
                  ? "cursor-not-allowed"
                  : "cursor-pointer hover:bg-[#F3F7FB]"
              }`}
            >
              <input
                type="checkbox"
                data-testid={`monev-assign-opt-ppid-${item.code}`}
                checked={draft.all || draft.ppid || !draft.keys.length}
                disabled={ppidLocked}
                onChange={() =>
                  setDraft((d) => ({ ...d, all: false, ppid: !d.ppid }))
                }
                className="h-4 w-4 accent-[#1479BF]"
              />
              <span className="text-xs font-semibold text-[#0B2440]">
                PPID (isi sendiri)
              </span>
            </label>
            <label className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors duration-150 hover:bg-[#F3F7FB]">
              <input
                type="checkbox"
                data-testid={`monev-assign-opt-all-${item.code}`}
                checked={draft.all}
                onChange={() =>
                  setDraft((d) => ({ all: !d.all, ppid: false, keys: [] }))
                }
                className="h-4 w-4 accent-[#1479BF]"
              />
              <span className="text-xs font-semibold text-[#0B2440]">
                Semua Pengelola
              </span>
            </label>
            <div className="my-2 border-t border-[#E3EBF3]" />
            {divisions.map((a) => (
              <label
                key={a.key}
                className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors duration-150 hover:bg-[#F3F7FB]"
              >
                <input
                  type="checkbox"
                  data-testid={`monev-assign-opt-${a.key}-${item.code}`}
                  checked={draft.keys.includes(a.key)}
                  onChange={() => toggleKey(a.key)}
                  className="h-4 w-4 accent-[#1479BF]"
                />
                <span className="text-xs font-semibold text-slate-600">
                  {a.label}
                </span>
              </label>
            ))}
            {ppidLocked && (
              <p className="mt-2 text-[10px] leading-relaxed text-slate-400">
                PPID selalu dapat mengisi isian ini.
              </p>
            )}
            <button
              type="button"
              data-testid={`monev-assign-save-${item.code}`}
              disabled={!dirty || waiting}
              onClick={save}
              className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#1479BF] px-3 py-2 text-[11px] font-bold text-white transition-colors duration-200 hover:bg-[#0B2440] disabled:cursor-not-allowed disabled:opacity-40"
            >
              {waiting ? (
                <Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check aria-hidden="true" className="h-3.5 w-3.5" />
              )}
              Simpan
            </button>
          </div>
        </>
      )}
    </div>
  );
}
